import {
    ApolloCache,
    ApolloClient,
    useApolloClient,
} from '@thoughtspot/apollo-utils';
import { ErrorContext, ErrorContextType } from '@thoughtspot/blink-context';
import { BLINK_FLAGS, envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { isDataLimitExceedError } from '@thoughtspot/ts-chart-app';
import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useBachLazyQuery, useBachQuery } from '/@services/bach/bach';
import { bachSessionWithAcSessionKeyQuery } from '/@services/bach/bach.util';
import {
    useGatedBachLazyQuery,
    useHydrationGate,
} from '/@services/bach/bach-gated';
import {
    DataQueryInput,
    GetAnswerDataByQueryDocument,
    GetAnswerDataByQueryQuery,
    GetAnswerDataByQueryQueryVariables,
    GetVizDataDocument,
    GetVizDataQuery,
    GetVizDataQueryVariables,
} from '/@services/generated/graphql-types';
import {
    getAnswerDataQueryAbortSubject,
    registerCancelId,
    unregisterCancelId,
} from '/@services/search/answer-cancellation-manager';
import {
    createAnswerDataQueryCancelId,
    getCancelableDataQueryContext,
    isAbortQueryError,
} from '/@services/search/search-cancel.util';
import { getSession } from '/@services/stateful/stateful-service-hooks.util';
import { getErrorObjectFromGraphQLErrors } from '/@utils/error-handling/error-utils';

export const shouldCombineMultipleQueriesForCustomChart = () => {
    return isFeatureEnabled(
        FEATURE_FLAG_NAMES.COMBINE_MULTIPLE_QUERIES_FOR_CUSTOM_CHART,
        {
            urlParamValue: envFlags.getValue(
                BLINK_FLAGS.combineMultipleQueriesForCustomChart?.name,
            ),
        },
    );
};

/**
 * Bounds the pagination size of every query in a DataQueryInput to
 * `maxBatchSize`: fills it in when a query omits the size (default) and clamps
 * it when a query requests more (cap). This gives custom-chart fetches via
 * getAnswerDataByQuery the same batch-size limit native charts get from
 * GetChartWithData. Returns the input unchanged when `maxBatchSize` is not a
 * positive finite number.
 */
export const clampDataQueryPaginationSize = (
    dataQueries: DataQueryInput,
    maxBatchSize?: number,
): DataQueryInput => {
    if (
        !dataQueries?.queries?.length ||
        maxBatchSize == null ||
        !Number.isFinite(maxBatchSize) ||
        maxBatchSize <= 0
    ) {
        return dataQueries;
    }
    return {
        ...dataQueries,
        queries: dataQueries.queries.map(query => {
            const pagination = query.queryParam?.pagination;
            return {
                ...query,
                queryParam: {
                    ...query.queryParam,
                    pagination: {
                        ...pagination,
                        size: Math.min(
                            pagination?.size ?? maxBatchSize,
                            maxBatchSize,
                        ),
                    },
                },
            };
        }),
    };
};

export function useGetVizDataQueryByVizIds(
    answerSessionId: string,
    vizIds: string[],
    skip: boolean,
) {
    const { data, loading } = useBachQuery<
        GetVizDataQuery,
        GetVizDataQueryVariables
    >(answerSessionId, GetVizDataDocument, {
        variables: {
            vizIds,
        } as GetVizDataQueryVariables,
        skip,
    });
    return {
        vizData: data?.getVizData?.vizData,
        loading,
    };
}

export function useLazyGetVizDataQueryByVizIds(answerSessionId: string) {
    const [_getVizData, { loading }] = useGatedBachLazyQuery<
        GetVizDataQuery,
        GetVizDataQueryVariables
    >(answerSessionId, GetVizDataDocument, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    });
    return {
        getVizData: (vizIds: string[]) =>
            _getVizData({
                variables: {
                    vizIds,
                } as GetVizDataQueryVariables,
            }),
        loading,
    };
}

const getMaxRowCount = (data: GetAnswerDataByQueryQuery) => {
    const mappedData = data.getAnswerDataByQuery.data.map(
        dataArr => dataArr[0],
    );
    const maxRowCount = Math.max(...mappedData.map(item => item.totalRowCount));
    return maxRowCount;
};

export const getParsedQueryResponseForAnswerDataByQueryCall = (
    data: GetAnswerDataByQueryQuery,
) => {
    /**
        The response object is of following format
        responseObject = {
            getAnswerDataByQuery: {
                data: [
                    First Query Response -> [
                        {
                            "columnDataLite": [
                                {},
                                {},
                                ...
                            ],
                            "completionRatio": 1,
                            "samplingRatio": 1,
                            "totalRowCount": "8"
                        }
                    ],
                    Second Query Response -> [
                        {
                            "columnDataLite": [
                                {},
                                {},
                                ...
                            ],
                            "completionRatio": 1,
                            "samplingRatio": 1,
                            "totalRowCount": "8"
                        }
                    ]
                ]
            }
        }
    */
    const mappedData = data.getAnswerDataByQuery.data.map(
        dataArr => dataArr[0],
    );

    const parsedData = _.map(mappedData, dataRow => {
        return {
            ...dataRow,
            columnDataLite: _.map(dataRow.columnDataLite, colRow => ({
                ...colRow,
                dataValue: JSON.parse(colRow.dataValue),
            })),
        };
    });
    return parsedData;
};

export function useLazyGetAnswerDataByQuery(
    answerSessionId: string,
    isQueryCancellationEnabled = false,
) {
    const client = useApolloClient();

    const errorCtx = useContext<ErrorContextType<string>>(ErrorContext);

    const { gateCall } = useHydrationGate();

    // Always reflects the latest answerSessionId so the gateCall lambda can
    // re-read the session after a background LCB refresh replaces it. Without
    // this, the session captured at enqueue time (stale, invalidated by
    // loadPinboardAnswer during viz edit) is sent when the gate opens.
    const answerSessionIdRef = useRef(answerSessionId);
    answerSessionIdRef.current = answerSessionId;

    // Dedicated cancel id for the custom-chart data fetch. It must NOT reuse the
    // chart's chartDataCancelId: that id is (un)registered by
    // useAnswerDataQueryCancellation based on the chart-definition query's
    // loading, which goes false before getAnswerDataByQuery runs — so the chart
    // path would unregister our id mid-flight and data_cancel would miss it.
    const dataQueryCancelIdRef = useRef(createAnswerDataQueryCancelId());

    // Client-side abort for the in-flight data request(s). One controller is
    // shared across the chart's concurrent queries so a single cancel aborts them
    // all (like data_cancel cancelling every statement under the trace id).
    // Apollo `refetch` cannot take per-call context — it reuses the LAST render's
    // context — so both the signal and the id must be minted at render: re-mint
    // here once aborted (the effect below aborts and forces a re-render, never
    // swaps) so refetch's frozen context always carries a live signal. The id is
    // re-minted together with the controller so a rerun after a cancel never
    // reuses an id already dispatched to data_cancel (a stale backend cancel
    // racing in could kill the new query). Header id and registered id both come
    // from this same render snapshot, so they stay consistent.
    const abortControllerRef = useRef<AbortController>(
        new window.AbortController(),
    );
    if (
        isQueryCancellationEnabled &&
        abortControllerRef.current.signal.aborted
    ) {
        dataQueryCancelIdRef.current = createAnswerDataQueryCancelId();
        abortControllerRef.current = new window.AbortController();
    }

    // Read AFTER the re-mint guard so the re-mint render already uses the fresh
    // id in both the context header and registration.
    const cancelId = isQueryCancellationEnabled
        ? dataQueryCancelIdRef.current
        : undefined;

    // Per-id refcount of in-flight fetches: register on every fetch start
    // (Set-add is idempotent), unregister an id when ITS last fetch settles.
    // Per-id (not a single shared count) because a cancel re-mints the id, so
    // fetches with different ids can overlap — a shared count would let the old
    // id's settle unregister nothing while the new id strands in the manager.
    // Registration lives here (not the chart-def query's loading) because for
    // custom charts that query completes before the data fetch runs.
    const inFlightCountsRef = useRef(new Map<string, number>());

    // Bumped on every cancel signal. A network call captures this before the
    // hydration gate and bails if it changed by the time the gate opens. Without
    // it, a cancel that fires while the gate is still pending (nothing in flight,
    // so the abort subscription has nothing to abort, and data_cancel dispatches
    // before this id is registered) would let the deferred query run to
    // completion against the backend after the answer is already cancelled.
    const cancelGenerationRef = useRef(0);

    // Forces a render after a cancel. Some cancel flows (X-clear while execution
    // is Idle) change no external state — every answer-context write bails as
    // unchanged — so without this bump the render-time re-mint above never runs
    // and every later refetch would carry the frozen aborted signal (instant
    // AbortError, absorbed into a never-settling promise: the chart's data
    // fetching would be dead until an unrelated render).
    const [, bumpCancelRender] = useState(0);

    const { refetch } = useBachQuery<
        GetAnswerDataByQueryQuery,
        GetAnswerDataByQueryQueryVariables
    >(
        answerSessionId,
        GetAnswerDataByQueryDocument,
        {
            /**
             * We want all the data calls to be network only as we are not preserving data states.
             * Data states are not preserved as client(chart app) can call queries at random also
             * which will not be part of the actual queries. Hence chart is expected to maintain
             * data states.
             * Open Item: How will the filters and other question updates trigger chart renders?
             */
            fetchPolicy: 'cache-first',
            skip: true,
            // Cancel id → X-Callosum-Trace-Id header (backend data_cancel) +
            // abort signal (client-side teardown). Empty context when disabled.
            // Apollo refetch cannot take per-call context; context flows only
            // through render — hence the render-time re-mint and the forced
            // re-render on cancel above.
            context: getCancelableDataQueryContext(
                cancelId ? abortControllerRef.current.signal : undefined,
                cancelId,
            ),
        },
        null,
        true,
        true,
    );

    // On a user cancel the shared abort subject fires: abort the in-flight
    // request(s) (guarded on the in-flight map — don't abort a controller nothing
    // is using). No swap here — the render-time guard above re-mints controller
    // and id once aborted; the bump below guarantees that render happens even
    // when the cancel flow changes no other state. Residual (accepted): a
    // chart-lib data event landing in the sub-millisecond window between abort()
    // and React's flush still dies absorbed — that one event is lost and the
    // chart recovers on the next event or chart-lib's render timeout.
    useEffect(() => {
        if (!isQueryCancellationEnabled) {
            return undefined;
        }
        const subscription = getAnswerDataQueryAbortSubject(
            answerSessionId,
        ).subscribe(() => {
            // Bump first so a gate-pending call (nothing to abort yet) still sees
            // the cancel and drops itself when the gate opens.
            cancelGenerationRef.current += 1;
            if (inFlightCountsRef.current.size > 0) {
                abortControllerRef.current.abort();
            }
            bumpCancelRender(generation => generation + 1);
        });
        return () => subscription.unsubscribe();
    }, [answerSessionId, isQueryCancellationEnabled]);

    return {
        getAnswerDataByQuery: (
            dataQueries: DataQueryInput,
            vizId: string,
            fetchFreshData = false,
            trackDataQueryEventForCustomChart: (
                dataQueryInput: DataQueryInput,
                totalRowCount: number,
                queryTimingMs?: number,
                isCacheHit?: boolean,
            ) => void,
        ): Promise<JSON[]> => {
            // Start timing for this query
            const startTime = performance.now();

            const combineQueries = shouldCombineMultipleQueriesForCustomChart();
            const session = getSession(
                client,
                answerSessionId,
                'BachSessionId',
                bachSessionWithAcSessionKeyQuery,
                ['acSession'],
            );

            // Try to read from cache using NORMALIZED query
            const cacheVariables = {
                session: {
                    acSession: session.acSession,
                },
                dataQueries,
                vizId,
                combineQueries,
            } as GetAnswerDataByQueryQueryVariables;

            const cachedQueryResponse = client.cache.readQuery({
                query: GetAnswerDataByQueryDocument,
                variables: cacheVariables,
            }) as GetAnswerDataByQueryQuery;

            if (!_.isNil(cachedQueryResponse) && !fetchFreshData) {
                const endTime = performance.now();
                const queryTimingMs = endTime - startTime;
                const maxRowCount = getMaxRowCount(cachedQueryResponse);
                trackDataQueryEventForCustomChart(
                    dataQueries,
                    maxRowCount,
                    queryTimingMs,
                    true, // cache hit
                );
                return Promise.resolve(
                    getParsedQueryResponseForAnswerDataByQueryCall(
                        cachedQueryResponse,
                    ),
                );
            }

            // Use NORMALIZED query for the network request too, so it gets cached with normalized key.
            // Wrap with gateCall so the refetch is deferred until isHydrated = true,
            // preventing NW_ERROR when called before the answer session is ready.
            // Decrement this id's in-flight count; unregister when ITS last
            // fetch settles. Ids are independent: an old (pre-cancel) id's
            // settle must never unregister — or leave stranded — the current id.
            // settleInFlightFetch takes the sessionId explicitly so that
            // register and unregister always use the same session — the one
            // the refetch actually sent to the backend (session C), not the
            // one captured at enqueue time (session B, possibly stale after a
            // background LCB refresh). sessionIdForCancel is set inside the
            // gateCall lambda once we know which session the refetch will use.
            const sessionIdAtEnqueue = answerSessionIdRef.current;
            const settleInFlightFetch = (id: string, sessionId: string) => {
                const counts = inFlightCountsRef.current;
                const remaining = (counts.get(id) ?? 0) - 1;
                if (remaining <= 0) {
                    counts.delete(id);
                    unregisterCancelId(sessionId, id);
                } else {
                    counts.set(id, remaining);
                }
            };
            // Mutable: updated inside the gateCall lambda to the session that
            // the refetch uses, so the .finally below can unregister from the
            // same session. Defaults to sessionIdAtEnqueue for the synchronous
            // (non-pending gate) path where both values are identical.
            let sessionIdForCancel = sessionIdAtEnqueue;

            const cancelGenerationAtEnqueue = cancelGenerationRef.current;
            return gateCall(() => {
                // Cancelled while waiting for hydration: drop the query without
                // issuing it. Return a never-settling promise (NOT a reject) so
                // the .finally below never runs — we bail before register/
                // increment, so a decrement there would unbalance the refcount.
                // This mirrors the container's cancel absorption (held until the
                // container unmounts on the Cancelled state).
                if (cancelGenerationRef.current !== cancelGenerationAtEnqueue) {
                    return new Promise<never>(() => undefined);
                }
                // Re-read the session at gate-open time. If a background LCB
                // refresh replaced the session while this call was queued
                // (e.g. after a liveboard refresh + viz edit close), the
                // closure's `session` would be the stale pre-edit session that
                // the backend already invalidated. Using the ref ensures we send
                // the fresh session that HYDRATED was emitted with.
                const currentSession = getSession(
                    client,
                    answerSessionIdRef.current,
                    'BachSessionId',
                    bachSessionWithAcSessionKeyQuery,
                    ['acSession'],
                );
                // Pair register and unregister (in .finally) to the same
                // session the refetch will use. Without this, if the session
                // advanced from B → C while the gate was pending, the cancelId
                // would be stored under session B while data_cancel looks it up
                // under session C — missing it and leaking the backend query.
                sessionIdForCancel = answerSessionIdRef.current;
                // Register only when the gate opens and we're about to hit the
                // network — not while pending. Brackets registration to the real
                // in-flight window (no phantom id in data_cancel) and stays
                // balanced with the .finally: a dropped deferred lambda runs
                // neither branch. Register unconditionally (Set-add is
                // idempotent): a cancel clears the manager's Set, so a fetch
                // overlapping a not-yet-settled aborted sibling must re-add the
                // id or a second cancel would miss it in data_cancel.
                if (cancelId) {
                    registerCancelId(sessionIdForCancel, cancelId);
                    inFlightCountsRef.current.set(
                        cancelId,
                        (inFlightCountsRef.current.get(cancelId) ?? 0) + 1,
                    );
                }
                try {
                    return refetch({
                        session: currentSession,
                        dataQueries,
                        vizId,
                        combineQueries,
                    });
                } catch (err) {
                    // A synchronous throw would escape gateCall before the
                    // .finally below attaches, stranding the refcount. Convert
                    // it to a rejection so the normal chain settles it.
                    return Promise.reject(err);
                }
            })
                .then(responseObject => {
                    const endTime = performance.now();
                    const queryTimingMs = endTime - startTime;
                    const maxRowCount = getMaxRowCount(responseObject.data);
                    trackDataQueryEventForCustomChart(
                        dataQueries,
                        maxRowCount,
                        queryTimingMs,
                        false, // network call
                    );
                    return getParsedQueryResponseForAnswerDataByQueryCall(
                        responseObject.data,
                    );
                })
                .catch(err => {
                    // AbortError from a user cancel / teardown: RE-THROW it as-is
                    // so the caller (chart-viz.container's getAnswerDataByQuery
                    // catch) can absorb it. It is NOT dropped here — this branch
                    // only keeps it from falling through to errorCtx.postError
                    // below (which would surface it as a chart error). The actual
                    // absorption is the second catch layer in the container.
                    // data_cancel has already stopped the backend.
                    if (isQueryCancellationEnabled && isAbortQueryError(err)) {
                        throw err;
                    }
                    const errors = getErrorObjectFromGraphQLErrors<string>(
                        err,
                        GetAnswerDataByQueryDocument,
                    );
                    const rootError = errors[0];
                    // Data limit errors need special handling to allow custom chart initialization
                    // Do NOT post them to the error context - they will be handled by ts-chart-app's error boundary
                    // This allows users to modify chart config/visual props to fix the issue
                    // (e.g., add aggregations, change column selections, reduce number of measures, add facets, etc.)
                    // If posted to error context, it would show as a generic error and prevent chart initialization
                    if (isDataLimitExceedError(rootError)) {
                        throw rootError;
                    }
                    errorCtx.postError(errors);
                    throw errors;
                })
                .finally(() => {
                    if (cancelId) {
                        settleInFlightFetch(cancelId, sessionIdForCancel);
                    }
                });
        },
    };
}
